<script setup lang="ts">
import {
  computed,
  defineProps,
  defineEmits,
  defineExpose,
  ref,
  watch,
  onMounted,
} from "vue";
import {
  Point,
  ImageNodeConfig,
  BoundingBox,
  MiddlePoint,
} from "@/types/stage";
import Konva from "konva";
import Circle = Konva.Circle;
import Stage = Konva.Stage;
import LoShooLines from "@/views/Calculators/ApartmentPlan/components/stage/LoShooLines.vue";
import MiddlePointComponent from "@/views/Calculators/ApartmentPlan/components/stage/MiddlePointComponent.vue";
import MiddleMountains from "@/views/Calculators/ApartmentPlan/components/stage/MiddleMountains.vue";
import BorderMountains from "@/views/Calculators/ApartmentPlan/components/stage/BorderMountains.vue";
import MountainsLabels from "@/views/Calculators/ApartmentPlan/components/stage/MountainsLabels.vue";
import PolygonPointsLayer from "@/views/Calculators/ApartmentPlan/components/stage/PolygonPointsLayer.vue";
import DiagramImageLayer from "@/views/Calculators/ApartmentPlan/components/stage/DiagramImageLayer.vue";
import AzimuthArrow from "@/views/Calculators/ApartmentPlan/components/stage/AzimuthArrow.vue";
import ApartmentFlyingStarsLayer from "@/views/Calculators/ApartmentPlan/components/stage/ApartmentFlyingStarsLayer.vue";
import {
  baseStarsAngles,
  ContainerProperties,
  useStarsPositions,
} from "@/composables/useStarsPositions";
import { AptDateParamsResponse, IImageConfig } from "@/types/apartmentPlan";
import { ToggleKey } from "@/constants/apartmentPlan";
import diagramImageSrc from "@/assets/calculators/room-mapping/diagram.png";

const props = defineProps<{
  stageWidth: number;
  stageHeight: number;
  azmFacade: number;
  buildYear: number | null;
  facadeDirection: number | null;
  points: Point[];
  imageNode: ImageNodeConfig;
  diagramDateParams: AptDateParamsResponse | undefined;
  diagramScale: number;
  movePointsMode: boolean;
  toggles: Record<ToggleKey, boolean>;
}>();

const emit = defineEmits<{
  (event: "point:add", e: Stage): void;
  (event: "point:move", index: number, e: Circle): void;
  (event: "point:remove", index: number, e: Circle): void;
}>();

const stageRef = ref(null);
defineExpose({
  stageRef,
});

const diagramImage = ref<HTMLImageElement | null>(null);

function loadDiagramImageAsset() {
  const img = new Image();
  img.onload = () => {
    diagramImage.value = img;
  };
  img.src = diagramImageSrc;
}

onMounted(() => {
  loadDiagramImageAsset();
});

const boundingBox = computed<BoundingBox | null>(() => {
  if (props.points.length < 4) return null;

  // 1. Найти крайние координаты
  const xs = props.points.map((p) => p.x);
  const ys = props.points.map((p) => p.y);

  const expandFactor = 30;
  const minX = Math.min(...xs) - expandFactor;
  const minY = Math.min(...ys) - expandFactor;
  const maxX = Math.max(...xs) + expandFactor;
  const maxY = Math.max(...ys) + expandFactor;

  // 2. Размеры прямоугольника
  const width = maxX - minX;
  const height = maxY - minY;

  return {
    minX: minX,
    minY: minY,
    maxX: maxX,
    maxY: maxY,
    width: width,
    height: height,
  };
});

const middlePoint = computed<MiddlePoint | null>(() => {
  if (!boundingBox.value) return null;

  const { minX, minY, width, height } = boundingBox.value;

  return {
    x: minX + width / 2,
    y: minY + height / 2,
  };
});

const rotateAngle = computed(() => {
  if (props.facadeDirection === null) {
    return props.azmFacade;
  }
  return props.facadeDirection - props.azmFacade;
});

const diagramImageConfig = computed<IImageConfig | null>(() => {
  if (!boundingBox.value || !middlePoint.value) {
    return null;
  }

  const { width, height } = boundingBox.value;
  const { x, y } = middlePoint.value;

  let boxSize = Math.max(width, height);
  const maxX = x - boxSize / 2 + boxSize;
  if (maxX > props.stageWidth) {
    boxSize -= maxX - props.stageWidth;
  } else if (maxX > props.stageHeight) {
    boxSize -= maxX - props.stageHeight;
  }
  const maxY = y - boxSize / 2 + boxSize;
  if (maxY > props.stageHeight) {
    boxSize -= maxY - props.stageHeight;
  } else if (maxY > props.stageWidth) {
    boxSize -= maxY - props.stageWidth;
  }
  const facadeDirection = props.facadeDirection ?? 0;

  return {
    image: diagramImage.value,
    x: x,
    y: y,
    width: boxSize,
    height: boxSize,
    offsetY: boxSize / 2,
    offsetX: boxSize / 2,
    rotation: facadeDirection - props.azmFacade,
    scaleX: props.diagramScale,
    scaleY: props.diagramScale,
  };
});

const getStarsContainerProps = computed<ContainerProperties | null>(() => {
  if (!diagramImageConfig.value) {
    return null;
  }
  const { x, y, width } = diagramImageConfig.value;
  return {
    centerX: x,
    centerY: y,
    radius: (width / 2) * props.diagramScale + 50,
  };
});

const { starsPositions, getStarIndex, centerAngle } = useStarsPositions(
  rotateAngle,
  () => getStarsContainerProps.value
);

watch(
  () => props.toggles["lo-shoo"],
  (showLoShoo: boolean) => {
    if (!showLoShoo) {
      centerAngle.value = 0;
      return;
    }
    let minDelta = Infinity;
    let targetAngle = Infinity;
    for (const baseAngle of baseStarsAngles) {
      const angleDelta = Math.abs(baseAngle - props.azmFacade);
      if (angleDelta < minDelta) {
        minDelta = angleDelta;
        targetAngle = baseAngle;
      }
    }
    centerAngle.value = props.azmFacade - targetAngle;
  }
);

function onStageClick(e: Stage) {
  if (e.evt && e.evt.button === 0) {
    emit("point:add", e);
  }
}
</script>

<template>
  <div class="w-auto h-auto">
    <v-stage
      ref="stageRef"
      :config="{ width: stageWidth, height: stageHeight }"
      @click="onStageClick"
      @tap="(e: Stage) => emit('point:add', e)"
      @contextmenu="(e: Stage) => e.evt.preventDefault()"
    >
      <!-- BACKGROUND LAYER -->
      <v-layer>
        <v-rect
          :config="{
            x: 0,
            y: 0,
            width: stageWidth,
            height: stageHeight,
            fill: '#FFFFFF',
          }"
        />
      </v-layer>
      <!-- USER IMAGE LAYER -->
      <v-layer>
        <v-image :config="imageNode" />
      </v-layer>
      <PolygonPointsLayer
        :points="points"
        :movePointsMode="movePointsMode"
        @point:move="(i: number, e: Circle) => emit('point:move', i, e)"
        @point:remove="(i: number, e: Circle) => emit('point:remove', i, e)"
      />
      <v-layer>
        <MiddlePointComponent :middlePoint="middlePoint" />
        <LoShooLines v-if="toggles['lo-shoo']" :boundingBox="boundingBox" />
        <MiddleMountains
          v-if="toggles['middle-12-mountains']"
          :boundingBox="boundingBox"
        />
        <BorderMountains
          v-if="toggles['border-12-mountains']"
          :boundingBox="boundingBox"
        />
        <MountainsLabels
          v-if="
            toggles['middle-12-mountains'] || toggles['border-12-mountains']
          "
          :moveLabelsMode="toggles['move-mountains-labels']"
          :boundingBox="boundingBox"
        />
        <AzimuthArrow
          :facadeDirection="facadeDirection"
          :boundingBox="boundingBox"
          :middlePoint="middlePoint"
        />
      </v-layer>
      <v-layer>
        <DiagramImageLayer
          :diagramConfig="diagramImageConfig"
          :diagramDateParams="diagramDateParams"
          :toggles="toggles"
        />
        <ApartmentFlyingStarsLayer
          v-if="buildYear && facadeDirection !== null"
          :buildYear="buildYear"
          :azimuthFacade="azmFacade"
          :getStarIndex="getStarIndex"
          :starsPositions="starsPositions"
          :aptStars="
            toggles['current-stars'] ? diagramDateParams?.stars : undefined
          "
        />
      </v-layer>
    </v-stage>
  </div>
</template>
